import * as React from 'react';
import styled from 'styled-components';
import Lightbox from '../lightbox/Lightbox';
import { OnlineThemeType } from '../../themes/online';
import { PopupTheme } from '../../enums/common';
import SLinkButton from '../linkButton/SLinkButton';
import { imageIcon } from '../ImageIcon/ImageIcon';
import { withTheme } from '../themeProvider/ThemeProvider';

function imageIconStyle(theme: OnlineThemeType, popupTheme: PopupTheme) {
  switch (popupTheme) {
    case PopupTheme.danger:
      return `
      width: 75px;
      height: 67px;
      background-image: url(${theme.icons.error});
      `;

    case PopupTheme.success:
    default:
      return `
      width: 62px;
      height: 62px;
      background-image: url(${theme.icons.success});
      `;
  }
}

function buttonStyle(theme: OnlineThemeType, popupTheme: PopupTheme) {
  switch (popupTheme) {
    case PopupTheme.danger:
      return `
      color: ${theme.colors.error2};
      `;

    case PopupTheme.success:
    default:
      return `
        color: ${theme.colors.light};
      `;
  }
}

const SPopup = styled.section<{ customHeight?: string }>(
  ({ theme, customHeight }) => `
  background-color: ${theme.colors.white};
  box-shadow: 0 0 28px 16px rgba(0, 0, 0, 0.12);
  padding-top: 27px;
  box-sizing: border-box;
  text-align: center;

  @media only screen and ${theme.device.laptopS} {
    width: 300px;
    border-radius: 5px;
    box-sizing: border-box;
    ${customHeight ? `height: ${customHeight}` : ''}
  }
`
);
const SPopupImg = styled.div<{ popupTheme: PopupTheme }>(
  ({ theme, popupTheme }) => `
  ${imageIconStyle(theme, popupTheme)}
  margin: 0 auto;
`
);
const SPopupTitle = styled.h2(
  ({ theme }) => `
 color: ${theme.colors.primaryDark2};
 font-size:  ${theme.font.size.medium2};
 font-weight: ${theme.font.weight.bold};
 line-height: 1.04;
 margin: 8px 0;
 `
);

const SPopupText = styled.div<{ withTitle: boolean }>(
  ({ theme, withTitle }) => `
   padding: 0 38px;
   font-size: 17px;
   font-weight: 300;
   line-height: 1.21;
   color: ${theme.colors.grayLight2};
   margin-top: ${withTitle ? 0 : '24px'};
`
);

// tslint:disable-next-line:variable-name
const SButton = styled.button<{ popupTheme: PopupTheme }>(
  ({ theme, popupTheme }) => `
  border: none;
  cursor: pointer;
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 29px;
  padding: 22px 0;
  border-top: 1px solid rgba(133,149,159, 0.4);
  background-color: transparent;
  font-weight: bold;
  font-size: 17px;
  line-height: 1.56;
  ${buttonStyle(theme, popupTheme)};

  &:hover {
    opacity: 0.5;
  }
  `
);

const SBackToPage = styled(SLinkButton as any)(({ theme }) => ({
  width: '100%',
  padding: '0 38px',
  '&:focus': { outline: 'none' },
  '&:before': {
    ...imageIcon({ icon: theme.icons.darkBlueLeftArrow, height: 24, width: 24 }),
  },
}));

export interface IPopup {
  className?: string;
  customHeight?: string;
  onClose?: () => void;
  hidePopupButton?: boolean;
  title?: string;
  button?: string;
  theme: PopupTheme;
  dataAt: string;
  noLightBox?: boolean;
}

// tslint:disable-next-line:variable-name
const Popup: React.FC<IPopup> = ({
  onClose,
  children,
  customHeight,
  className,
  button = 'OK',
  title = '',
  theme,
  dataAt,
  hidePopupButton,
  noLightBox,
}) => {
  const handleClose = React.useCallback(() => {
    onClose?.();
  }, [onClose]);

  const popup = (
    <SPopup data-at={dataAt} data-testid={dataAt} customHeight={customHeight} className={className}>
      {hidePopupButton && <SBackToPage onClick={handleClose} />}
      {theme && <SPopupImg popupTheme={theme}></SPopupImg>}
      <SPopupTitle data-at="popup-title">{title}</SPopupTitle>
      <SPopupText className="popup-message" data-at="popup-message" withTitle={title.length > 0}>
        {children}
      </SPopupText>
      {Boolean(onClose) && !hidePopupButton && (
        <SButton popupTheme={theme} onClick={handleClose} data-at="popup-action">
          {button}
        </SButton>
      )}
    </SPopup>
  );

  if (noLightBox) return popup;

  return <Lightbox children={popup} />;
};

export default withTheme(Popup);
